import { RoleFunction, Time } from '@eagle/common';
import { AccountType } from '@eagle/core-data-types';
import { Button, Divider, ListItemButton, Popover, Stack, styled, TextField, Typography } from '@ethings/ui-toolkit';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useDebounce } from '@react-hook/debounce';
import { useQuery } from '@tanstack/react-query';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, useAuthenticated } from '../../auth';
import { API_CALL_TEXT_LENGTH, LOOKUP_DEBOUNCE_TIME } from '../../constants';
import { useCustomRoutes } from '../../hooks';
import { scrollbar } from '../../style';
import { CacheDataTypes, Nullable } from '../../types';
import { FILTER_OUT, switchToPortal, testid, trackEvent, useHasAuthorization } from '../../util';
import { FetchOneOfAll } from '../fetch';
import { useBoolFlag } from '../flags';
import { MiddleSpinner } from '../middle-spinner';

export const MAX_ACCOUNTS = 5;

const ADMIN_ROLE = [RoleFunction.ADMINISTRATOR] as const;
const ACCOUNT_ADMIN_ROLE = [RoleFunction.ACCOUNT_ADMINISTRATOR] as const;

interface AccountSwitcherProps {
  anchorEl: Nullable<HTMLElement>;
  onClose: VoidFunction;
  onSelect?: (value: string) => void;
}

const StyledPopover = styled(Popover)({
  zIndex: 1400,
});

const AccountMenuItem = styled(ListItemButton)({
  paddingLeft: '16px',
  paddingRight: '16px',
});

export const AccountSwitcher: FC<AccountSwitcherProps> = ({ anchorEl, onClose, onSelect }) => {
  const { recentAccounts } = useAuth();

  const open = Boolean(anchorEl);

  return (
    <StyledPopover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      onClick={(e) => { e.stopPropagation(); }}
    >
      <AccountSwitcherInner
        onClose={onClose}
        onSelect={(value) => {
          onClose();
          onSelect?.(value);
        }}
        recentAccounts={recentAccounts}
      />
    </StyledPopover>);
};

interface AccountSwitcherInnerProps {
  onSelect: (value: string) => void;
  onClose: VoidFunction;
  recentAccounts: string[];
}

interface RequestParams {
  filter: Record<string, any>;
  limit?: number;
  search?: string;
  searchFields?: string[];
}

const AccountSwitcherInner: FC<AccountSwitcherInnerProps> = ({ onClose, onSelect, recentAccounts }) => {
  const { t } = useTranslation(['common']);
  const { restClient, userInfo } = useAuthenticated();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useDebounce('', LOOKUP_DEBOUNCE_TIME);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const [isShowingMore, setIsShowingMore] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const isSearching = debouncedSearch.length > 0;
  const hasNoRecentAccounts = recentAccounts.length === 0 || (recentAccounts.length === 1 && recentAccounts[0] === userInfo.baseAccountId);
  const customRoutes = useCustomRoutes();
  const hasManageAccountsFeature = useBoolFlag('manage-accounts-feature');
  const { hasAuthorization } = useHasAuthorization();

  const hasMinimumSearchLength = debouncedSearch.length >= API_CALL_TEXT_LENGTH;

  const { data: accounts, status: accountStatus } = useQuery({
    queryKey: ['account-switcher-accounts', debouncedSearch, recentAccounts, userInfo.baseAccountId, isSearching, hasNoRecentAccounts, hasMinimumSearchLength],
    staleTime: Time.seconds(15),
    queryFn: async () => {
      const params: RequestParams = {
        filter: FILTER_OUT.deleted,
      };
      if (!isSearching) {
        if (hasNoRecentAccounts) {
          params.filter = { ...params.filter, _id: { $ne: userInfo.baseAccountId } };
          params.limit = MAX_ACCOUNTS;
        }
        else {
          params.filter = { ...params.filter, _id: { $in: recentAccounts, $ne: userInfo.baseAccountId } };
        }
      }
      else if (hasMinimumSearchLength) {
        params.search = debouncedSearch;
        params.searchFields = ['display'];
        params.filter = { ...params.filter, _id: { $ne: userInfo.baseAccountId } };
      }
      else {
        return null;
      }

      const accounts = await restClient.my.accounts.getAll({
        ...params,
      });

      if (!isSearching && !hasNoRecentAccounts) {
        accounts.sort((a, b) => recentAccounts.findIndex((id) => a._id === id) - recentAccounts.findIndex((id) => b._id === id));
      }

      if (params.search) {
        trackEvent('keyword_search', 'returned_results', 'account_switcher', { 'results_type': 'account', 'results_count': accounts.length, 'search_term': params.search });
      }

      return accounts;
    },
  });

  useEffect(() => {
    if (!isSmallScreen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isSmallScreen]);

  const displayRecentLabel = !isSearching && !hasNoRecentAccounts;
  const displaySearchHint = isSearching && !hasMinimumSearchLength;
  const displayResults = !isSearching || hasMinimumSearchLength;
  const isLoading = displayResults && accountStatus !== 'success';
  const displayShowMoreButton = displayResults && (accounts && accounts.length > MAX_ACCOUNTS);
  const displayAllAccountsLink = hasAuthorization(ADMIN_ROLE) && hasAuthorization(ACCOUNT_ADMIN_ROLE) && hasManageAccountsFeature;

  const visibleAccounts = isShowingMore ? accounts : accounts?.slice(0, MAX_ACCOUNTS);

  return (
    <Stack maxHeight={isShowingMore ? 465 : 350} width={250}>
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="caption">{t('common:component.switch-account.switch-account.labels')}</Typography>
        <TextField
          data-testid="account-switcher-input"
          fullWidth
          inputRef={searchRef}
          inputProps={{
            'aria-label': t('common:component.lookup.hint.placeholder'),
          }}
          onChange={(e) => {
            setSearch(e.target.value);
            setDebouncedSearch(e.target.value);
          }}
          onFocus={() => {
            trackEvent('keyword_search', 'focus_input', 'account_switcher');
          }}
          placeholder={t('common:component.lookup.hint.placeholder')}
          size="small"
          value={search}
          variant="standard"
        />
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', ...scrollbar }}>
        {displayRecentLabel && <Typography variant="caption" px={2}>{t('common:component.switch-account.recent.labels')}</Typography>}
        {displaySearchHint && <Typography px={2} pt={0} pb={1} variant="body2" data-testid="account-switcher-minimum-character">{t('common:component.search.hint.less-than-count', { count: API_CALL_TEXT_LENGTH })}</Typography>}

        {!isLoading ? (
          <div>
            {visibleAccounts?.length === 0 && <Typography px={2} pt={0} pb={1} variant="body2" data-testid="account-switcher-no-result">{t('common.hint.list.no-results')}</Typography>}
            {visibleAccounts?.map((account, index) => (
              <AccountMenuItem
                data-testid={testid`account-switcher-item-${account._id}`}
                key={account._id}
                onClick={() => {
                  onSelect(account._id);
                  trackEvent('list', 'selected_item', 'account_switcher_list', { 'selected_item': index ? (index + 1) : 1 });
                }}
              >
                <Stack direction='column'>
                  <Typography variant='subtitle2'>{account.display}</Typography>
                  {account.accountTypeId
                    ? <FetchOneOfAll
                      dataType={CacheDataTypes.MY_ACCESSIBLE_ACCOUNT_TYPE}
                      id={account.accountTypeId}
                      renderFactory={(accountType: AccountType) => <Typography variant='caption'>{accountType.display}</Typography>}
                    />
                    : <Typography color="text.secondary" fontStyle="italic" variant="caption">
                      {t('common:page.accounts-list.no-account-type.labels')}
                    </Typography>
                  }
                </Stack>
              </AccountMenuItem>
            ))}
          </div>
        ) : (
          <MiddleSpinner sx={{ py: 2 }} size={20} />
        )}
      </Box>

      {displayShowMoreButton && (
        <Button data-testid="account-switcher-show-more-button" onClick={() => { setIsShowingMore((value) => !value); }}>
          {isShowingMore ? t('common:component.switch-account.show-less.labels') : t('common:component.switch-account.show-more.labels')}
        </Button>
      )}
      {displayAllAccountsLink && (
        <Box sx={{ mt: 'auto' }}>
          <Divider />
          <AccountMenuItem
            data-testid="account-show-all-accounts-button"
            onClick={() => {
              onClose();
              const nextLocation = switchToPortal(
                null,
                'admin',
                search ? `${customRoutes.accounts}?searchText=${encodeURIComponent(search)}` : `${customRoutes.accounts}`,
              );
              if (nextLocation) {
                window.location.assign(nextLocation);
              }
            }}
          >
            {t('common:component.switch-account.show-all-accounts.labels')}
          </AccountMenuItem>
        </Box>
      )}
    </Stack>
  );
};
